/**
 * Institution Detail Page (SUPER_ADMIN)
 *
 * Vista 360° de una IPS con tabs: Información | Suscripción | Pagos |
 * Insumos. El tab Insumos permite ver el aprovisionamiento real (study
 * types y priorities) que tiene la IPS, agregar custom y aprovisionar
 * desde el catalogo global.
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Receipt,
  Layers,
  Loader2,
  Edit,
  Plus,
  Trash2,
  PackagePlus,
  Activity,
  Users,
  HardDrive,
  FileText,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/presentation/components/ui/Tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useInstitution,
  useOnboardInstitution,
  useGlobalCatalog,
} from '@/application/institutions'
import {
  useInstitutionSubscription,
  usePayments,
} from '@/application/subscriptions/useSubscriptions'
import {
  useInstitutionStudyTypes,
  useInstitutionPriorities,
  useCreateInstitutionStudyType,
  useCreateInstitutionPriority,
  useDeleteInstitutionStudyType,
  useDeleteInstitutionPriority,
} from '@/application/institution-insumos/useInstitutionInsumos'
import { useInstitutionUsage } from '@/application/institution-usage/useInstitutionUsage'
import { SubscriptionModal } from '@/presentation/components/admin/SubscriptionModal'
import {
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
  type SubscriptionStatus,
} from '@/domain/subscription/types'
import { APP_ROUTES } from '@/config/constants'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'

const formatDate = (iso: string | null | Date | undefined): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatCop = (n: number): string =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(n)

export default function InstitutionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState('info')
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)

  const institutionQuery = useInstitution(id ?? '')
  const subscriptionQuery = useInstitutionSubscription(id ?? null)
  const paymentsQuery = usePayments(id ?? null)

  if (institutionQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  if (!institutionQuery.data) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--foreground-muted)]">Institucion no encontrada</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(APP_ROUTES.SUPER_ADMIN.INSTITUTIONS)}
        >
          Volver al listado
        </Button>
      </div>
    )
  }

  const inst = institutionQuery.data
  const subscription = subscriptionQuery.data?.subscription
  const noSubscription =
    subscriptionQuery.isError &&
    (subscriptionQuery.error as { statusCode?: number; status?: number })?.statusCode === 404

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={APP_ROUTES.SUPER_ADMIN.INSTITUTIONS}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            {inst.name}
          </h1>
          <p className="text-[var(--foreground-muted)] text-sm">
            {inst.slug} · {inst.city ?? 'Sin ciudad'}
          </p>
        </div>
        <Badge variant={inst.isActive ? 'default' : 'secondary'}>
          {inst.isActive ? 'Activa' : 'Inactiva'}
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="info">
            <Building2 className="h-4 w-4 mr-2" />
            Informacion
          </TabsTrigger>
          <TabsTrigger value="subscription">
            <CreditCard className="h-4 w-4 mr-2" />
            Suscripcion
          </TabsTrigger>
          <TabsTrigger value="payments">
            <Receipt className="h-4 w-4 mr-2" />
            Pagos
          </TabsTrigger>
          <TabsTrigger value="usage">
            <Activity className="h-4 w-4 mr-2" />
            Uso
          </TabsTrigger>
          <TabsTrigger value="insumos">
            <Layers className="h-4 w-4 mr-2" />
            Insumos
          </TabsTrigger>
        </TabsList>

        {/* Informacion */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Datos de la institucion</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-[var(--foreground-muted)]">Slug</dt>
                  <dd className="font-mono">{inst.slug}</dd>
                </div>
                <div>
                  <dt className="text-[var(--foreground-muted)]">NIT</dt>
                  <dd>{inst.nit ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-[var(--foreground-muted)]">Ciudad</dt>
                  <dd>{inst.city ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-[var(--foreground-muted)]">Telefono</dt>
                  <dd>{inst.phone ?? '—'}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-[var(--foreground-muted)]">Direccion</dt>
                  <dd>{inst.address ?? '—'}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-[var(--foreground-muted)]">Email</dt>
                  <dd>{inst.email ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-[var(--foreground-muted)]">Creada</dt>
                  <dd>{formatDate(inst.createdAt)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suscripcion */}
        <TabsContent value="subscription">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Suscripcion vigente</CardTitle>
              <Button size="sm" onClick={() => setSubscriptionModalOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Gestionar
              </Button>
            </CardHeader>
            <CardContent>
              {subscriptionQuery.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
              ) : noSubscription || !subscription ? (
                <div className="text-sm text-[var(--foreground-muted)]">
                  Esta institucion no tiene suscripcion. Asigna un plan desde el
                  boton "Gestionar".
                </div>
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
                        Plan
                      </p>
                      <p className="text-lg font-semibold">{subscription.plan.name}</p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {subscription.plan.code} ·{' '}
                        {formatCop(subscription.plan.monthlyPriceCop)}/mes
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SUBSCRIPTION_STATUS_COLORS[subscription.status as SubscriptionStatus]}`}
                    >
                      {SUBSCRIPTION_STATUS_LABELS[subscription.status as SubscriptionStatus]}
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <dt className="text-[var(--foreground-muted)]">Inicio</dt>
                      <dd>{formatDate(subscription.startsAt)}</dd>
                    </div>
                    {subscription.trialEndsAt && (
                      <div>
                        <dt className="text-[var(--foreground-muted)]">Fin de trial</dt>
                        <dd>{formatDate(subscription.trialEndsAt)}</dd>
                      </div>
                    )}
                    {subscription.expiresAt && (
                      <div>
                        <dt className="text-[var(--foreground-muted)]">Vencimiento</dt>
                        <dd>{formatDate(subscription.expiresAt)}</dd>
                      </div>
                    )}
                    {subscription.gracePeriodEndsAt && (
                      <div>
                        <dt className="text-[var(--foreground-muted)]">Fin cortesia</dt>
                        <dd>{formatDate(subscription.gracePeriodEndsAt)}</dd>
                      </div>
                    )}
                  </dl>
                  {subscription.suspendedReason && (
                    <div className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 p-2 text-sm text-red-900 dark:text-red-100">
                      <strong>Suspendida:</strong> {subscription.suspendedReason}
                    </div>
                  )}
                  {subscription.notes && (
                    <div className="text-xs text-[var(--foreground-muted)] whitespace-pre-line border-l-2 border-[var(--border)] pl-2">
                      <strong>Notas:</strong>
                      {'\n'}
                      {subscription.notes}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pagos */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>
                Historial de pagos {paymentsQuery.data && `(${paymentsQuery.data.total})`}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {paymentsQuery.isLoading ? (
                <div className="p-6">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
                </div>
              ) : !paymentsQuery.data || paymentsQuery.data.payments.length === 0 ? (
                <p className="p-6 text-center text-[var(--foreground-muted)]">
                  Sin pagos registrados.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha de pago</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Periodo</TableHead>
                      <TableHead>Metodo</TableHead>
                      <TableHead>Referencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentsQuery.data.payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(p.paidAt)}</TableCell>
                        <TableCell className="font-medium">
                          {formatCop(p.amountCop)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatDate(p.periodStart)} → {formatDate(p.periodEnd)}
                        </TableCell>
                        <TableCell className="text-xs">{p.paymentMethod ?? '—'}</TableCell>
                        <TableCell className="text-xs font-mono">
                          {p.reference ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Uso */}
        <TabsContent value="usage">
          <UsageTab institutionId={inst.id} />
        </TabsContent>

        {/* Insumos */}
        <TabsContent value="insumos">
          <InsumosTab institutionId={inst.id} />
        </TabsContent>
      </Tabs>

      <SubscriptionModal
        institution={inst}
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
      />
    </div>
  )
}

// =============================================================================
// Tab de Insumos — gestion del aprovisionamiento de la IPS
// =============================================================================

function InsumosTab({ institutionId }: { institutionId: string }) {
  const studyTypesQuery = useInstitutionStudyTypes(institutionId)
  const prioritiesQuery = useInstitutionPriorities(institutionId)
  const globalCatalogQuery = useGlobalCatalog(true)

  const onboardMutation = useOnboardInstitution()
  const createStudyType = useCreateInstitutionStudyType(institutionId)
  const createPriority = useCreateInstitutionPriority(institutionId)
  const deleteStudyType = useDeleteInstitutionStudyType(institutionId)
  const deletePriority = useDeleteInstitutionPriority(institutionId)

  const [provisionDialogOpen, setProvisionDialogOpen] = useState(false)
  const [createStudyTypeOpen, setCreateStudyTypeOpen] = useState(false)
  const [createPriorityOpen, setCreatePriorityOpen] = useState(false)
  const [studyTypeForm, setStudyTypeForm] = useState({
    code: '',
    name: '',
    description: '',
  })
  const [priorityForm, setPriorityForm] = useState({
    code: '',
    name: '',
    color: '#EF4444',
    sortOrder: 1,
  })
  const [pickedStudyTypes, setPickedStudyTypes] = useState<Set<string>>(new Set())
  const [pickedPriorities, setPickedPriorities] = useState<Set<string>>(new Set())

  const studyTypes = studyTypesQuery.data?.studyTypes ?? []
  const priorities = prioritiesQuery.data?.priorities ?? []
  const globalCatalog = globalCatalogQuery.data

  const studyTypeCodesAssigned = new Set(studyTypes.map((s) => s.code))
  const priorityCodesAssigned = new Set(priorities.map((p) => p.code))

  // Solo mostramos los globales que aun NO estan asignados a la IPS
  const availableStudyTypes =
    globalCatalog?.studyTypes.filter((s) => !studyTypeCodesAssigned.has(s.code)) ?? []
  const availablePriorities =
    globalCatalog?.priorities.filter((p) => !priorityCodesAssigned.has(p.code)) ?? []

  const handleProvision = () => {
    if (pickedStudyTypes.size === 0 && pickedPriorities.size === 0) {
      toast.error('Selecciona al menos un insumo para aprovisionar')
      return
    }

    onboardMutation.mutate(
      {
        id: institutionId,
        data: {
          copyStudyTypes: pickedStudyTypes.size > 0,
          copyPriorities: pickedPriorities.size > 0,
          studyTypeCodes: Array.from(pickedStudyTypes),
          priorityCodes: Array.from(pickedPriorities),
        },
      },
      {
        onSuccess: (res) => {
          toast.success(
            `${res.studyTypesCopied} tipos de estudio y ${res.prioritiesCopied} prioridades aprovisionados`
          )
          setProvisionDialogOpen(false)
          setPickedStudyTypes(new Set())
          setPickedPriorities(new Set())
          studyTypesQuery.refetch()
          prioritiesQuery.refetch()
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  const handleCreateStudyType = () => {
    if (!studyTypeForm.code || !studyTypeForm.name) {
      toast.error('Codigo y nombre son requeridos')
      return
    }
    createStudyType.mutate(
      {
        code: studyTypeForm.code,
        name: studyTypeForm.name,
        description: studyTypeForm.description || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Tipo de estudio creado')
          setCreateStudyTypeOpen(false)
          setStudyTypeForm({ code: '', name: '', description: '' })
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  const handleCreatePriority = () => {
    if (!priorityForm.code || !priorityForm.name) {
      toast.error('Codigo y nombre son requeridos')
      return
    }
    createPriority.mutate(
      {
        code: priorityForm.code,
        name: priorityForm.name,
        color: priorityForm.color,
        sortOrder: priorityForm.sortOrder,
      },
      {
        onSuccess: () => {
          toast.success('Prioridad creada')
          setCreatePriorityOpen(false)
          setPriorityForm({ code: '', name: '', color: '#EF4444', sortOrder: 1 })
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Aprovisionamiento de la IPS</span>
            <Button
              size="sm"
              onClick={() => setProvisionDialogOpen(true)}
              disabled={availableStudyTypes.length === 0 && availablePriorities.length === 0}
            >
              <PackagePlus className="h-4 w-4 mr-2" />
              Aprovisionar desde catalogo global
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[var(--foreground-muted)]">
            Insumos asignados especificamente a esta IPS. Los insumos del
            catalogo global son visibles para todos y no aparecen aqui — solo
            cuentan los que fueron aprovisionados o creados para esta institucion.
          </p>
        </CardContent>
      </Card>

      {/* Tipos de estudio */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tipos de estudio ({studyTypes.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setCreateStudyTypeOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Crear custom
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {studyTypesQuery.isLoading ? (
            <div className="p-6">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : studyTypes.length === 0 ? (
            <p className="p-6 text-center text-[var(--foreground-muted)]">
              Sin tipos de estudio asignados a esta IPS. Aprovisiona desde el
              catalogo global o crea uno custom.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codigo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Descripcion</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[80px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {studyTypes.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.code}</TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sm text-[var(--foreground-muted)]">
                      {s.description ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.isActive ? 'default' : 'secondary'}>
                        {s.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`¿Eliminar "${s.name}"?`)) {
                            deleteStudyType.mutate(s.id, {
                              onSuccess: () => toast.success('Eliminado'),
                              onError: (err) => toast.error(getErrorMessage(err)),
                            })
                          }
                        }}
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4 text-[var(--destructive)]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Prioridades */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Prioridades ({priorities.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setCreatePriorityOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Crear custom
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {prioritiesQuery.isLoading ? (
            <div className="p-6">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : priorities.length === 0 ? (
            <p className="p-6 text-center text-[var(--foreground-muted)]">
              Sin prioridades asignadas a esta IPS.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codigo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Orden</TableHead>
                  <TableHead className="w-[80px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {priorities.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.code}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: p.color, color: p.textColor }}
                      >
                        {p.code}
                      </span>
                    </TableCell>
                    <TableCell>{p.sortOrder}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`¿Eliminar prioridad "${p.name}"?`)) {
                            deletePriority.mutate(p.id, {
                              onSuccess: () => toast.success('Eliminada'),
                              onError: (err) => toast.error(getErrorMessage(err)),
                            })
                          }
                        }}
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4 text-[var(--destructive)]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Aprovisionar desde catalogo global */}
      <Dialog open={provisionDialogOpen} onOpenChange={setProvisionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Aprovisionar desde catalogo global</DialogTitle>
            <DialogDescription>
              Selecciona los insumos que deseas copiar a la institucion. Solo se
              muestran los que aun no estan asignados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Tipos de estudio disponibles ({availableStudyTypes.length})
              </h3>
              <div className="space-y-1">
                {availableStudyTypes.map((s) => (
                  <label
                    key={s.code}
                    className="flex items-center gap-2 p-2 rounded hover:bg-[var(--background-muted)] cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={pickedStudyTypes.has(s.code)}
                      onChange={(e) => {
                        const next = new Set(pickedStudyTypes)
                        if (e.target.checked) next.add(s.code)
                        else next.delete(s.code)
                        setPickedStudyTypes(next)
                      }}
                    />
                    <span className="font-mono text-xs">{s.code}</span>
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Prioridades disponibles ({availablePriorities.length})
              </h3>
              <div className="space-y-1">
                {availablePriorities.map((p) => (
                  <label
                    key={p.code}
                    className="flex items-center gap-2 p-2 rounded hover:bg-[var(--background-muted)] cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={pickedPriorities.has(p.code)}
                      onChange={(e) => {
                        const next = new Set(pickedPriorities)
                        if (e.target.checked) next.add(p.code)
                        else next.delete(p.code)
                        setPickedPriorities(next)
                      }}
                    />
                    <span className="font-mono text-xs">{p.code}</span>
                    <span>{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProvisionDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleProvision} disabled={onboardMutation.isPending}>
              {onboardMutation.isPending ? 'Aprovisionando...' : 'Aprovisionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear study type custom */}
      <Dialog open={createStudyTypeOpen} onOpenChange={setCreateStudyTypeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear tipo de estudio</DialogTitle>
            <DialogDescription>
              Custom para esta IPS. No se replica a otras instituciones.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                placeholder="Ej. RX-TORAX"
                value={studyTypeForm.code}
                onChange={(e) =>
                  setStudyTypeForm({ ...studyTypeForm, code: e.target.value.toUpperCase() })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                placeholder="Ej. Radiografia de Torax"
                value={studyTypeForm.name}
                onChange={(e) => setStudyTypeForm({ ...studyTypeForm, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion (opcional)</label>
              <Input
                value={studyTypeForm.description}
                onChange={(e) =>
                  setStudyTypeForm({ ...studyTypeForm, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateStudyTypeOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateStudyType} disabled={createStudyType.isPending}>
              {createStudyType.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear prioridad custom */}
      <Dialog open={createPriorityOpen} onOpenChange={setCreatePriorityOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear prioridad</DialogTitle>
            <DialogDescription>Custom para esta IPS.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                placeholder="Ej. URGENTE"
                value={priorityForm.code}
                onChange={(e) =>
                  setPriorityForm({ ...priorityForm, code: e.target.value.toUpperCase() })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                placeholder="Ej. Urgente (en 4 horas)"
                value={priorityForm.name}
                onChange={(e) => setPriorityForm({ ...priorityForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Color (hex)</label>
                <Input
                  type="color"
                  value={priorityForm.color}
                  onChange={(e) => setPriorityForm({ ...priorityForm, color: e.target.value })}
                  className="h-10"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Orden</label>
                <Input
                  type="number"
                  min={0}
                  value={priorityForm.sortOrder}
                  onChange={(e) =>
                    setPriorityForm({ ...priorityForm, sortOrder: Number(e.target.value) })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePriorityOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreatePriority} disabled={createPriority.isPending}>
              {createPriority.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// =============================================================================
// Tab de Uso — metricas operativas y comparativa vs limites del plan
// =============================================================================

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`
}

const formatRelativeDate = (iso: string | null): string => {
  if (!iso) return 'Nunca'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (days === 0) return 'Hoy'
  if (days === 1) return 'Ayer'
  if (days < 30) return `Hace ${days} dias`
  return d.toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' })
}

function UsageTab({ institutionId }: { institutionId: string }) {
  const { data, isLoading } = useInstitutionUsage(institutionId)

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  const { users, patients, studies, reports, storage, trend, lastActivity, plan } = data

  // Comparativa de estudios vs mes pasado
  const studiesGrowth =
    studies.lastMonth === 0
      ? null
      : Math.round(((studies.thisMonth - studies.lastMonth) / studies.lastMonth) * 100)

  return (
    <div className="space-y-6">
      {plan && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 p-3 text-sm text-blue-900 dark:text-blue-100">
          Plan vigente: <strong>{plan.name}</strong> ({plan.code}). Las barras de
          uso comparan contra los limites de este plan; "ilimitado" significa que
          el plan no impone restriccion en esa dimension.
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <UsageCard
          icon={<Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
          label="Usuarios"
          value={users.total.toLocaleString('es-CO')}
          subtitle={`${users.activeLast30d} activos en 30d`}
        />
        <UsageCard
          icon={<Users className="h-5 w-5 text-green-600 dark:text-green-400" />}
          label="Pacientes"
          value={patients.total.toLocaleString('es-CO')}
          subtitle={`+${patients.newLast30d} en 30d`}
        />
        <UsageCard
          icon={<FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
          label="Estudios este mes"
          value={studies.thisMonth.toLocaleString('es-CO')}
          subtitle={
            studiesGrowth === null
              ? 'sin comparativa'
              : `${studiesGrowth >= 0 ? '+' : ''}${studiesGrowth}% vs mes pasado`
          }
        />
        <UsageCard
          icon={<HardDrive className="h-5 w-5 text-orange-600 dark:text-orange-400" />}
          label="Storage"
          value={`${storage.totalGb} GB`}
          subtitle={formatBytes(storage.totalBytes)}
        />
      </div>

      {/* Comparativa contra limites del plan */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uso vs limites del plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <UsageBar
            label="Usuarios"
            current={users.total}
            max={users.max}
            unit=""
          />
          <UsageBar
            label="Estudios este mes"
            current={studies.thisMonth}
            max={studies.max}
            unit=""
          />
          <UsageBar
            label="Almacenamiento"
            current={storage.totalGb}
            max={storage.maxGb}
            unit="GB"
          />
        </CardContent>
      </Card>

      {/* Grafico de estudios por dia */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estudios procesados (ultimos 30 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {trend.length === 0 ? (
            <p className="text-sm text-[var(--foreground-muted)]">
              Sin actividad reciente.
            </p>
          ) : (
            <DailyTrendChart data={trend} />
          )}
        </CardContent>
      </Card>

      {/* Reportes y ultima actividad */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Reportes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Total historico</span>
              <span className="font-medium">{reports.total.toLocaleString('es-CO')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Creados ultimos 30d</span>
              <span className="font-medium">{reports.last30d}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Firmados ultimos 30d</span>
              <span className="font-medium">{reports.signedLast30d}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ultima actividad</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Ultimo login</span>
              <span className="font-medium">
                {formatRelativeDate(lastActivity.lastLoginAt)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Ultimo estudio</span>
              <span className="font-medium">
                {formatRelativeDate(lastActivity.lastStudyAt)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--foreground-muted)]">Ultimo reporte</span>
              <span className="font-medium">
                {formatRelativeDate(lastActivity.lastReportAt)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function UsageCard({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: React.ReactNode
  label: string
  value: string
  subtitle: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          {icon}
          <span className="text-2xl font-bold">{value}</span>
        </div>
        <p className="text-xs text-[var(--foreground-muted)] mt-1">{subtitle}</p>
      </CardContent>
    </Card>
  )
}

function UsageBar({
  label,
  current,
  max,
  unit,
}: {
  label: string
  current: number
  max: number | null
  unit: string
}) {
  const isUnlimited = max === null
  const pct = isUnlimited ? 0 : max === 0 ? 0 : Math.min((current / max) * 100, 100)
  const overLimit = !isUnlimited && current > (max ?? 0)
  const colorClass = overLimit
    ? 'bg-red-500'
    : pct > 85
      ? 'bg-yellow-500'
      : 'bg-green-500'

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-medium">
          {current.toLocaleString('es-CO')}
          {unit ? ` ${unit}` : ''}
          {isUnlimited ? (
            <span className="text-[var(--foreground-muted)] ml-1">/ ilimitado</span>
          ) : (
            <span className="text-[var(--foreground-muted)] ml-1">
              / {max?.toLocaleString('es-CO')}
              {unit ? ` ${unit}` : ''}
            </span>
          )}
        </span>
      </div>
      {!isUnlimited && (
        <div className="h-2 rounded-full bg-[var(--border)] overflow-hidden">
          <div className={`h-full ${colorClass}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {overLimit && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          Excede el limite del plan en {(current - (max ?? 0)).toLocaleString('es-CO')}{' '}
          {unit}
        </p>
      )}
    </div>
  )
}

function DailyTrendChart({ data }: { data: Array<{ date: string; count: number }> }) {
  const maxCount = data.reduce((m, d) => Math.max(m, d.count), 0)
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-24">
        {data.map((d, i) => {
          const height = maxCount === 0 ? 0 : (d.count / maxCount) * 100
          return (
            <div
              key={i}
              className="flex-1 bg-purple-500 rounded-t hover:bg-purple-600 transition-colors min-w-[3px]"
              style={{ height: `${Math.max(height, 2)}%` }}
              title={`${d.date}: ${d.count} estudios`}
            />
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-[var(--foreground-muted)]">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  )
}
